import { useEffect, useState } from 'react';
import { Autocomplete, AutocompleteRenderInputParams, Box, IconButton, Text } from '@hopskipdrive/react-components';

import { LocationPinIcon, SchoolBuildingIcon } from '../../../../shared/components/Icons';
import { colorTokens } from '../../../../shared/components/variables';
import { ACCOUNT_LOCATIONS_RESOURCE_TYPE, PLACES_RESOURCE_TYPE } from '../useLocationOptions';

interface LocationOption {
  label: string;
  value: string;
  [x: string]: string | number | boolean | string[];
}

export interface LocationSearchDropdownProps {
  id: string;
  options: LocationOption[];
  isFetching: boolean;
  inputValue: string;
  setInputValue: (value: LocationOption) => void;
  popupIcon?: React.ReactNode;
  renderInput?: (params: AutocompleteRenderInputParams) => JSX.Element;
  onChange?: (event: React.SyntheticEvent, value: LocationOption | null) => void;
}

export const LocationSearchDropdown = ({
  id,
  options,
  isFetching,
  inputValue,
  setInputValue,
  ...props
}: LocationSearchDropdownProps) => {
  const [value, setValue] = useState<LocationOption | null>(null);
  const [locationInputValue, setLocationInputValue] = useState<string>('');

  useEffect(() => {
    inputValue && setLocationInputValue(inputValue);
  }, [inputValue]);

  return (
    <Autocomplete
      id={id}
      freeSolo
      options={options}
      isLoading={isFetching}
      filterOptions={(options) => options}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || '')}
      fullWidth
      inputValue={locationInputValue}
      value={value || ''}
      onInputChange={(_, value) => {
        const location = options.find((o) => o.label === value);

        if (location) {
          setInputValue(location);
        }

        setLocationInputValue(value);
      }}
      onChange={(_, value) => {
        if (!value || typeof value === 'string') {
          return;
        }

        setLocationInputValue(value.label);
        setValue(value);
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement | HTMLDivElement> & { key: string };
        const isFirstOption = options.findIndex((o) => o.label === option.label) === 0;

        return (
          <>
            {isFirstOption && (
              <Box
                key={`${key}-header`}
                component="li"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: `1px solid ${colorTokens.border.light}`,
                  padding: '16px',
                  marginBottom: '8px',
                  gap: '32px',
                }}
              >
                <Box display="flex" alignItems="center" gap="6px">
                  <IconButton size="small" style={{ background: colorTokens.info.main }}>
                    <SchoolBuildingIcon style={{ color: colorTokens.info.contrast, width: '12px', height: '12px' }} />
                  </IconButton>
                  <Text variant="subtitle2" style={{ color: colorTokens.text.secondary }}>
                    - Account Locations
                  </Text>
                </Box>
                <Box display="flex" alignItems="center" gap="6px">
                  <IconButton size="small" style={{ background: colorTokens.text.secondary }}>
                    <LocationPinIcon style={{ color: colorTokens.info.contrast, width: '12px', height: '12px' }} />
                  </IconButton>
                  <Text variant="subtitle2" style={{ color: colorTokens.text.secondary }}>
                    - Other Locations
                  </Text>
                </Box>
              </Box>
            )}
            <li key={key} {...optionProps}>
              {option?.resourceType === ACCOUNT_LOCATIONS_RESOURCE_TYPE && (
                <IconButton size="small" style={{ background: colorTokens.info.main, marginRight: '6px' }}>
                  <SchoolBuildingIcon style={{ color: colorTokens.info.contrast, width: '12px', height: '12px' }} />
                </IconButton>
              )}
              {option?.resourceType === PLACES_RESOURCE_TYPE && (
                <IconButton size="small" style={{ background: colorTokens.text.secondary, marginRight: '6px' }}>
                  <LocationPinIcon style={{ color: colorTokens.info.contrast, width: '12px', height: '12px' }} />
                </IconButton>
              )}
              {option.label}
            </li>
          </>
        );
      }}
      {...props}
    />
  );
};
